// calclar-5e2a8d14 — C1 "calendar clarity" (founder decisions, Marcus 2026-10-07; redesigned after
// the first local test round). Pure helpers for the day-view calendar (CalendarView.js).
//
// COPY RULE (Marcus): always use names, always state the result, never say "everyone else".
//
// THE ONE TRUTH FOR THE WORDING. Every consequence line ("Ben stays on this job.", "The job becomes
// unassigned.") is computed by running buildReassignment — the exact function the write uses — on
// the option's own (removeUid, addUid), and naming whoever is left. So the dialog can never claim a
// result the write won't produce. (Round 1 computed the wording separately, the calendar dropped two
// of its fields on the way to the dialog, and every option said "Nobody is left".)
//
// WHY THERE CAN BE SEVERAL "Remove only …" OPTIONS. The calendar library reports where a job was
// DROPPED, never which column it was dragged FROM (its sourceResource is dead code — see the
// CalendarView comment above planReassign). A job with two visible assignees is ONE event drawn in
// both columns, so "the person dragged from" is unknowable; guessing from screen position could
// remove the wrong person silently. So when one visible person is on the job there is one
// "Remove only" option, and when several are visible there is one per person.
//
// Guarantees kept from Seam 3B: a person with clocked time (a source:'clocked' labour row) is
// never removed by a drag — not by "Remove only", not by "Remove all".

export const REMOVE_EVERYONE = '__remove_everyone__';

// ── the assignment record (moved verbatim from CalendarView.js; regression-tested against it) ──

// Founder decision D3b-2. A row with source === 'clocked' can never be removed by the
// job form (mergeLaborForSave, JobFormModal.js:332-337 force-keeps them), so removing
// such a person here would be undone the next time anyone saved the job. 108 of 408
// production jobs carry one. Only the six server punch sites ever write 'clocked'.
export const hasClockedRow = (job, uid) =>
    (Array.isArray(job?.jobCosting?.laborEntries) ? job.jobCosting.laborEntries : [])
        .some(e => e && e.userId === uid && e.source === 'clocked');

// The labour rows to start from. When a job has none, rebuild them from `assignedTo`
// exactly as JobFormModal.reconstructLaborEntriesFromAssignments does (:305-316) —
// otherwise a job created by the Weekly Planner, a route group or mobile (91 of 408
// production jobs have empty labour rows) would lose its OTHER assignees on save.
export const baseLaborRows = (job, employeeList) => {
    const live = Array.isArray(job?.jobCosting?.laborEntries) ? job.jobCosting.laborEntries : [];
    if (live.length > 0) return live;
    const names = Array.isArray(job?.assignedToNames) ? job.assignedToNames : [];
    return (Array.isArray(job?.assignedTo) ? job.assignedTo : [])
        .filter(Boolean)
        .map((uid, i) => ({
            userId: uid,
            name: names[i] || (employeeList || []).find(e => e.id === uid)?.name || '',
            rate: null,
            hours: 0,
        }));
};

// Remove one person (or, with REMOVE_EVERYONE, everyone without clocked time), add another,
// leave everyone else exactly as they are. Returns the rows plus the two derived mirrors; each
// call site shapes them for its own write (a dotted `jobCosting.laborEntries` for updateDoc so
// sibling jobCosting fields survive, a nested object for addDoc).
export const buildReassignment = (job, removeUid, addUid, employeeList) => {
    let rows = baseLaborRows(job, employeeList);
    if (removeUid === REMOVE_EVERYONE) rows = rows.filter(r => r?.source === 'clocked');
    else if (removeUid) rows = rows.filter(r => r?.userId !== removeUid);
    if (addUid && !rows.some(r => r?.userId === addUid)) {
        rows = [...rows, {
            userId: addUid,
            name: (employeeList || []).find(e => e.id === addUid)?.name || '',
            rate: null,
            hours: 0,
        }];
    }
    return {
        rows,
        assignedTo: rows.map(r => r.userId).filter(Boolean),
        assignedToNames: rows.map(r => r.name || ''),
    };
};

// ── wording ───────────────────────────────────────────────────────────────────────────────

export const HIDDEN_SUFFIX = ' (hidden by your Team filter)';

export const joinNames = (names) => (names.length <= 1 ? (names[0] || '')
    : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`);

// "job #1857", or job "Lawn care" when the job has no number yet (a projected visit).
export const jobLabelFor = (job, formatJobNumber) => {
    const num = formatJobNumber ? formatJobNumber(job?.jobNumber) : (job?.jobNumber ? `#${job.jobNumber}` : '');
    return num ? `job ${num}` : `job "${job?.title || 'Untitled'}"`;
};
const capital = (s) => s.charAt(0).toUpperCase() + s.slice(1);

const hoursPhrase = (h) => {
    const n = Number(h) || 0;
    if (n <= 0) return 'time';
    const s = Number.isInteger(n) ? String(n) : String(Number(n.toFixed(2)));
    return `${s} ${n === 1 ? 'hour' : 'hours'}`;
};

function makeNamer({ job, employees, isHidden }) {
    const rows = baseLaborRows(job, employees);
    return (uid) => {
        const n = rows.find(r => r && r.userId === uid)?.name
            || (employees || []).find(e => e.id === uid)?.name || 'this person';
        return isHidden && isHidden(uid) ? `${n}${HIDDEN_SUFFIX}` : n;
    };
}

const clockedHours = (job, uid) => (Array.isArray(job?.jobCosting?.laborEntries) ? job.jobCosting.laborEntries : [])
    .filter(e => e && e.userId === uid && e.source === 'clocked')
    .reduce((s, e) => s + (Number(e.hours) || 0), 0);

const cantRemoveLine = (job, label, uid) => `${label} can't be removed because they logged ${hoursPhrase(clockedHours(job, uid))} on this job.`;

// Who is on the job after (removeUid, addUid) — straight from the write's own function.
const resultUids = (job, removeUid, addUid, employees) =>
    [...new Set(buildReassignment(job, removeUid, addUid, employees).assignedTo)];

/**
 * Everything the reassign dialog shows. Pure.
 *   mode       'unassign' (dropped on Unassigned) | 'toPerson' (dropped on targetId's column)
 *   shownUids  the job's assignees whose columns are on screen
 *   isHidden   uid → true when that person's column is hidden by the Team filter
 * Returns { ok:false, reason } or { ok:true, title, lines:[...], options:[{label, sub, removeUid, addUid}] }.
 */
export function buildReassignDialog({ mode, job, targetId, shownUids, employees, isHidden, formatJobNumber }) {
    const name = makeNamer({ job, employees, isHidden });
    const label = jobLabelFor(job, formatJobNumber);
    const shown = Array.isArray(shownUids) ? shownUids : [];
    const assignees = resultUids(job, null, null, employees);
    const everyone = [...new Set([...assignees, ...shown])];
    const clocked = everyone.filter(uid => hasClockedRow(job, uid));
    const stays = (uids) => (uids.length === 0 ? 'The job becomes unassigned.'
        : `${joinNames(uids.map(name))} ${uids.length === 1 ? 'stays' : 'stay'} on this job.`);
    const willBe = (uids) => (uids.length === 0 ? 'The job becomes unassigned.'
        : `${joinNames(uids.map(name))} will be on this job.`);

    if (mode === 'unassign') {
        if (shown.length === 0) return { ok: false, reason: 'There is no one to remove from this job.' };

        if (everyone.length <= 1) {
            const uid = shown[0];
            if (hasClockedRow(job, uid)) return { ok: false, reason: cantRemoveLine(job, name(uid), uid) };
            return {
                ok: true,
                title: `Unassign ${name(uid)} from ${label}?`,
                lines: [],
                options: [{ label: 'Unassign', sub: 'The job moves to Unassigned.', removeUid: uid, addUid: null }],
            };
        }

        const options = shown.filter(uid => !hasClockedRow(job, uid)).map(uid => ({
            label: `Remove only ${name(uid)}`,
            sub: stays(resultUids(job, uid, null, employees)),
            removeUid: uid,
            addUid: null,
        }));
        if (clocked.length === 0) {
            // Founder copy tweak: the label stays short — "(hidden)" — while the sub-lines and the
            // after-drop note keep the full "(hidden by your Team filter)".
            const short = (uid) => name(uid).replace(HIDDEN_SUFFIX, ' (hidden)');
            options.push({
                label: `Remove all: ${joinNames(everyone.map(short))}`,
                sub: stays(resultUids(job, REMOVE_EVERYONE, null, employees)),
                removeUid: REMOVE_EVERYONE,
                addUid: null,
            });
        }
        const lines = clocked.map(uid => cantRemoveLine(job, name(uid), uid));
        if (options.length === 0) return { ok: false, reason: lines.join(' ') };
        return { ok: true, title: `Move ${label} to Unassigned?`, lines, options };
    }

    // mode === 'toPerson' — dropped on someone who is not on the job (or not visibly so).
    const target = name(targetId);
    const replaceable = shown.filter(uid => !hasClockedRow(job, uid));
    const options = replaceable.map(uid => ({
        label: `Replace ${name(uid)} with ${target}`,
        sub: willBe(resultUids(job, uid, targetId, employees)),
        removeUid: uid,
        addUid: targetId,
    }));
    const keep = assignees.filter(uid => uid !== targetId);
    options.push({
        label: keep.length ? `Add ${target}, keep ${joinNames(keep.map(name))}` : `Add ${target}`,
        sub: keep.length ? 'Nobody is removed.' : `${target} will be the only person on this job.`,
        removeUid: null,
        addUid: targetId,
    });
    const lines = shown.filter(uid => hasClockedRow(job, uid)).map(uid => cantRemoveLine(job, name(uid), uid));
    return {
        ok: true,
        title: replaceable.length ? `Move ${label} to ${target}?` : `Add ${target} to ${label}?`,
        lines,
        options,
    };
}

/**
 * The note shown after a reassign drop: who is on the job now, and a "Show <name>" button for
 * each remaining person whose column the Team filter hides. Pure.
 */
export function afterDropNotice({ job, rows, employees, isHidden, formatJobNumber }) {
    const label = capital(jobLabelFor(job, formatJobNumber));
    const uids = [...new Set((rows || []).map(r => r && r.userId).filter(Boolean))];
    const plain = (uid) => (rows || []).find(r => r && r.userId === uid)?.name
        || (employees || []).find(e => e.id === uid)?.name || 'this person';
    if (uids.length === 0) return { text: `${label} is now unassigned.`, show: [] };
    const hidden = uids.filter(uid => isHidden && isHidden(uid));
    const show = hidden.map(uid => ({ uid, name: plain(uid) }));
    if (hidden.length === 0) return { text: `${label} is now assigned to ${joinNames(uids.map(plain))}.`, show };
    const hiddenNames = joinNames(hidden.map(plain));
    const be = hidden.length === 1 ? "who's" : "who are";
    if (hidden.length === uids.length) {
        return { text: `${label} is still assigned to ${hiddenNames}, ${be} hidden by your Team filter.`, show };
    }
    const visible = uids.filter(uid => !hidden.includes(uid));
    return {
        text: `${label} is now assigned to ${joinNames(visible.map(plain))}, and still assigned to ${hiddenNames}, ${be} hidden by your Team filter.`,
        show,
    };
}

// The quick view's "Assigned to" list, one entry per line: the people by name (labour rows are the
// assignment record, assignedToNames their mirror), else the crew, else "Nobody (unassigned)".
export function assignedToList(job) {
    const rows = Array.isArray(job?.jobCosting?.laborEntries) ? job.jobCosting.laborEntries : [];
    const fromRows = rows.map(r => r && r.name).filter(Boolean);
    const fromMirror = (Array.isArray(job?.assignedToNames) ? job.assignedToNames : []).filter(Boolean);
    const names = [...new Set(fromRows.length ? fromRows : fromMirror)];
    if (names.length) return names;
    if (job?.routeGroupName) return [`${job.routeGroupName} (crew)`];
    return ['Nobody (unassigned)'];
}

// Click-to-create in a person's day-view column: the new job opens already assigned to them.
export function seedForSlot({ start, end, resourceId, employees, unassignedId }) {
    const seed = { startAt: start, endAt: end };
    if (typeof resourceId === 'string' && resourceId && resourceId !== unassignedId) {
        const person = (employees || []).find(e => e.id === resourceId);
        if (person) {
            seed.assignedTo = [person.id];
            seed.assignedToNames = [person.name || ''];
        }
    }
    return seed;
}
